/**
 * Mock sgimacog — EEG baseline / impedance + raw preview in telerehab.
 *
 * Real sgimacog: 8ch BLE headset → WASM filter → impedance per ch +
 * raw 100 SPS preview + band power. In telerehab mode, no UI controls
 * shown to case (per Q9), just connection status. Impedance + raw
 * stream goes to trainer via BC.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DemoGuard, NotInProduction, isDemoHostAllowed } from "../components/DemoBanner.js";

const CHANNELS = ["Fp1", "Fp2", "F3", "F4", "C3", "Cz", "C4", "Pz"] as const;

export default function SgimacogDemoSubapp() {
  const [search] = useSearchParams();
  const room = search.get("room");
  const isTr = search.get("telerehab") === "1";

  const [running, setRunning] = useState(true);
  const [impedance, setImp]   = useState<Record<string, number>>(
    () => Object.fromEntries(CHANNELS.map((ch, i) => [ch, 5 + i * 1.2]))
  );
  const bcRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!isTr || !room) return;
    if (!isDemoHostAllowed()) return;
    const bc = new BroadcastChannel(`tr-${room}`);
    bcRef.current = bc;
    bc.onmessage = (ev) => {
      const m = ev.data;
      if (m.type === "start" || m.type === "resume") setRunning(true);
      if (m.type === "pause" || m.type === "stop")   setRunning(false);
    };
    bc.postMessage({ type: "subapp_state", state: "ready", source: "eeg-baseline" });
    return () => bc.close();
  }, [isTr, room]);

  // Drift impedance + emit
  useEffect(() => {
    if (!running) return;
    if (!isDemoHostAllowed()) return;
    const t = setInterval(() => {
      setImp(prev => {
        const next: Record<string, number> = {};
        for (const ch of CHANNELS) {
          const drift = (Math.random() - 0.5) * 0.4;
          next[ch] = Math.max(2, Math.min(25, prev[ch] + drift));
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running]);

  // Emit impedance + reward (proxy: % of channels < 10 kΩ). demo:true tags.
  useEffect(() => {
    if (!isDemoHostAllowed()) return;
    const bc = bcRef.current;
    if (!bc) return;
    const ts = Date.now();
    for (const [ch, kohm] of Object.entries(impedance)) {
      bc.postMessage({ type: "metric", source: `impedance.${ch}`, value: kohm, ts, demo: true });
    }
    const ok = Object.values(impedance).filter(v => v < 10).length;
    const reward = ok / CHANNELS.length;
    bc.postMessage({ type: "metric", source: "reward", value: reward, ts, demo: true });
  }, [impedance]);

  if (!isDemoHostAllowed()) return <NotInProduction subapp="sgimacog" />;

  if (!isTr) {
    return <main className="page page--auth"><h1>sgimacog Demo</h1><p className="muted">需要 ?telerehab=1&amp;room=...</p></main>;
  }

  return (
    <DemoGuard subapp="sgimacog · EEG baseline">
      <div style={{
        padding: "8px var(--s-5)", display: "flex", justifyContent: "space-between",
        background: "rgba(0,0,0,0.4)", color: "var(--paper)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        flexShrink: 0,
      }}>
        <span style={{ letterSpacing: "0.1em" }}>SGIMACOG · EEG baseline · TELEREHAB</span>
        <span>room {room?.slice(0, 12)}</span>
        <span style={{ color: running ? "var(--olive)" : "var(--warning)" }}>● {running ? "recording" : "paused"}</span>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    padding: "var(--s-7)" }}>
        <div className="serial" style={{ color: "var(--paper-deep)", marginBottom: "var(--s-5)" }}>
          連接狀態 · 8 channel
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 80px)",
          gap: "var(--s-3)",
        }}>
          {CHANNELS.map(ch => {
            const kohm = impedance[ch];
            const ok = kohm < 10;
            return (
              <div key={ch} style={{
                padding: "var(--s-3) var(--s-2)",
                textAlign: "center",
                background: ok ? "rgba(94, 116, 38, 0.18)" : "rgba(168, 51, 35, 0.35)",
                border: `1px solid ${ok ? "#3a4f1c" : "var(--danger)"}`,
                borderRadius: "var(--r-sm)",
              }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: "var(--t-sm)", color: "var(--paper)" }}>
                  {ch}
                </div>
                <div style={{ fontFamily: "var(--mono)", fontSize: "var(--t-md)", fontWeight: 600,
                              color: ok ? "var(--paper)" : "var(--paper)" }}>
                  {kohm.toFixed(1)}
                </div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 9, color: "var(--paper-deep)" }}>kΩ</div>
              </div>
            );
          })}
        </div>
        <div style={{
          marginTop: "var(--s-7)", color: "var(--paper-deep)",
          fontFamily: "var(--mono)", fontSize: "var(--t-xs)", letterSpacing: "0.1em",
        }}>
          BASELINE 錄製中 · 1000 SPS · 訊號傳給訓練師
        </div>
      </div>
    </DemoGuard>
  );
}
